<script setup lang="ts">
import { useColorMode } from "@vueuse/core";
import {
    myStores, isMyStoresLoading,
    // storesWithNegativeBalance
} from './MyStoresStore';
import { playBlankAudioLoop, isAbleToPlayAudio, watchNewOrders } from "./playAudio";
import { storeNotifierChannels } from "./listenStoreNotifier";
import { MySettingsStore } from "./MySettingsStore";
import DialogProvider from "@/ui-plus/dialog/DialogProvider.vue";
import { Toaster } from "@/ui-plus/sonner";
import { Package, Printer, Settings2, Loader2, Play, AlertCircle } from "lucide-vue-next";
import PairWidget from "./PairWidget.vue";
import { onMounted, ref } from "vue";
import { Button } from "@/components/ui/button";
import PrintOptionsDropdown from "./PrintOptionsDropdown.vue";
import {
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogScrollContent,
    DialogClose,
    DialogFooter
} from "@/components/ui/dialog";
import SettingsView from "./SettingsView.vue";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem
} from '@/components/ui/select';
import { currentPrinter, currentPrinters } from "./getPrinters";
import {
    NumberField,
    NumberFieldContent,
    NumberFieldDecrement,
    NumberFieldIncrement,
    NumberFieldInput,
} from '@/components/ui/number-field'
import { Label } from '@/components/ui/label';
import { copies, printAfterAccept } from "./copiesNumber";
import Dropdown from './Dropdown.vue';
import { Switch } from '@/components/ui/switch';
import { computed } from "vue";
import OrderCard from "./OrderCard.vue";
import { cachedStoreOrders } from "./RecentOrdersStore";

const mode = useColorMode();
const toasterTheme = computed(() => {
    if (mode.value === "auto") {
        return "system";
    } else {
        return mode.value;
    }
});

const orders = computed(() => {
    return Object.values(cachedStoreOrders.value).sort((a, b) => b.order_placed_at_ts - a.order_placed_at_ts);
});


onMounted(() => {
    playBlankAudioLoop();
    watchNewOrders();
});

const isSettingsDialogOpen = ref(false);

const handlePrinterChange = (deviceName: string) => {
    const selectedPrinter = currentPrinters.value.find(printer => printer.deviceName === deviceName);
    if (selectedPrinter) {
        currentPrinter.value = selectedPrinter;
    }
};

</script>

<template>
    <div>
        <div class="p-4 space-y-8 max-w-3xl mx-auto">

            <h1 class="text-3xl font-bold py-2">
                <Dropdown />
                Order Taking App
                <Package class="inline-block ml-2" />
            </h1>

            <div v-if="currentPrinters.length === 0"
                 class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold">
                <AlertCircle class="mr-2 inline-block" />
                No printers found!
                <br>
                Add a printer to your device then restart the app.
            </div>


            <div v-if="isMyStoresLoading">
                <div class="flex justify-center items-center h-screen">
                    <Loader2 class="w-20 h-20 animate-spin" />
                </div>
            </div>

            <!-- <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold text-xs"
                 v-for="store in storesWithNegativeBalance"
                 :key="store.id"
                 v-if="storesWithNegativeBalance.length > 0 && !isMyStoresLoading">
                <span class="text-xl">
                    Negative balance for '{{ store.name?.substring(0, 17) }}'
                </span>
                <br>
                Store balance is negative, so all payment methods except Stripe are disabled.
                <br>
                Please go to 'Dashboard' -> 'My Stores' -> '{{ store.name }}' -> 'Balance' page to top up your balance.
                <br>
                If Stripe is enabled, the next Stripe order will include a higher application fee to cover the negative
                balance automatically.
            </div> -->


            <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-2 text-sm"
                 v-if="!isMyStoresLoading && !isAbleToPlayAudio">
                To enable audio notifications, please click the button
                <br>
                or enable the audio permission in your browser settings.
                <br>
                <Button class="mt-2"
                        variant="outline"
                        @click="playBlankAudioLoop()">
                    <Play class="inline-block mr-1" />
                    Enable audio notifications
                </Button>
            </div>

            <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold"
                 v-if="myStores.length === 0 && !isMyStoresLoading">
                Not paired with any store
                <br>
                please pair with a store to start using
                <br>
                <PairWidget />
            </div>


            <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold"
                 v-if="myStores.length > 0 && Object.keys(storeNotifierChannels).length === 0 && !isMyStoresLoading">
                There is no store enabled.
                <br>
                Please open the settings and enable a store to receive orders in real time.
                <br>
                <Button @click="isSettingsDialogOpen = true;"
                        variant="outline">
                    <Settings2 class="inline-block mr-1" />
                    Settings
                </Button>
            </div>
            <!-- <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold"
                 v-if="myStores.length > 0 && Object.keys(storeNotifierChannels).length === 0 && !isMyStoresLoading">
                Connection error, will try to reconnect.
            </div> -->
            <!-- <div class="mb-4 border border-bg-foreground rounded-md p-4 font-bold">
                <AlertCircle class="mr-2 inline-block" />
                <span class="text-lg">Important:</span>
                Do not quit the app in your working hours to receive new orders instantly.
                <br>
                (you may close this window, closing this window will not quit the app)
                <br>
                <span class="text-sm text-muted-foreground">
                    If you do not accept orders after 25 minutes, they will be automatically cancelled and
                    refunded.
                </span>
            </div> -->
            <!-- <br>
            To prevent automatic cancellations, you can enable auto-acceptance in Settings → Store →
            Automations. -->

            <div class="flex gap-2 justify-between items-center">
                <Button variant="outline"
                        @click="isSettingsDialogOpen = true;"
                        class="mb-4">
                    <Settings2 class="inline-block mr-1" />
                    Settings
                </Button>
                <div
                     class="text-xs text-muted-foreground no-scrollbar overflow-x-auto overflow-y-hidden whitespace-nowrap">
                    Enabled Stores: {{myStores.filter(store => {
                        return MySettingsStore[store.id]?.isEnabled;
                    }).map(store => {
                        return `${store.name}`;
                    }).join(', ')}}
                </div>
            </div>

            <Dialog v-model:open="isSettingsDialogOpen">
                <DialogScrollContent class="w-full max-w-7xl">
                    <DialogHeader>
                        <DialogTitle>Settings</DialogTitle>
                        <DialogDescription />
                    </DialogHeader>

                    <div v-if="currentPrinters.length > 0"
                         class="mb-4">
                        <label class="block text-sm font-medium mb-2">Default Printer:</label>
                        <Select :model-value="currentPrinter?.deviceName"
                                @update:model-value="handlePrinterChange">
                            <SelectTrigger class="w-full">
                                <SelectValue placeholder="Select a printer" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="printer in currentPrinters"
                                            :key="printer.deviceName"
                                            :value="printer.deviceName">
                                    {{ printer.printerName || printer.deviceName }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div v-else
                         class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold">
                        <AlertCircle class="mr-2 inline-block" />
                        No printers found!
                        <br>
                        Add a printer to your device then restart the app.
                    </div>

                    <div class="space-y-2">
                        <Label>Copies</Label>
                        <NumberField id="copies"
                                     class="max-w-40 pt-8"
                                     v-model="copies"
                                     :default-value="1"
                                     :step="1"
                                     :min="1">
                            <NumberFieldContent>
                                <NumberFieldDecrement
                                                      class="bg-secondary rounded-l-md hover:bg-secondary/60 cursor-pointer" />
                                <NumberFieldInput />
                                <NumberFieldIncrement
                                                      class="bg-secondary rounded-r-md hover:bg-secondary/60 cursor-pointer" />
                            </NumberFieldContent>
                        </NumberField>
                    </div>

                    <div class="space-y-2">
                        <Label>Print after accept</Label>
                        <Switch v-model:checked="printAfterAccept" />
                    </div>

                    <div>
                        <PrintOptionsDropdown>
                            <template #trigger>
                                <Button variant="outline"
                                        class="mb-4">
                                    <Printer class="mr-1" />
                                    Size and boldness
                                </Button>
                            </template>
                        </PrintOptionsDropdown>
                    </div>


                    <PairWidget />

                    <SettingsView />

                    <DialogFooter>
                        <DialogClose as-child>
                            <Button variant="secondary"
                                    class="w-full">
                                Close
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogScrollContent>
            </Dialog>

            <div class="space-y-4">
                <div v-for="order in orders"
                     :key="order.storeOrderId"
                     class="block">
                    <OrderCard :order="order" />
                </div>
            </div>


        </div>
        <DialogProvider />
        <Toaster :theme="toasterTheme"
                 closeButton
                 richColors
                 class="pointer-events-auto" />
    </div>
</template>